import paths from '../routes/paths';

class MenuService {
  constructor() {
    this.currentMenus = null;
    this.userPermissions = null;
  }

  // Método principal para obtener menús
  async getUserMenus(userId) {
    try {
      // Importar authService aquí para evitar dependencias circulares
      const authService = await import('./authService');
      const result = await authService.default.getUserMenus();
      return result;
    } catch (error) {
      console.error('MenuService: Error obteniendo menús del usuario:', error);
      return { success: false, error: error.message };
    }
  }

  // Menús de FALLBACK cuando hay error de conexión (solo para usuarios autenticados)
  getFallbackMenus() {
    return [
      {
        name: 'Performance',
        icon: 'bullseye',
        active: true,
        children: [
          {
            name: 'Control de Presupuestos',
            to: paths.performanceMetrics,
            active: true
          },
          {
            name: 'Insertar Orden',
            to: paths.performanceInsertOrder,
            active: true
          },
          {
            name: 'Reportes',
            to: paths.performanceReports,
            active: true
          },
          {
            name: 'Analytics',
            to: paths.performanceAnalytics,
            active: true
          },
          {
            name: 'Objetivos',
            to: paths.performanceObjectives,
            active: true
          }
        ]
      },
      {
        name: 'Branding',
        icon: 'palette',
        active: true,
        children: [
          {
            name: 'Control de Presupuestos',
            to: paths.brandingBudgetControl,
            active: true
          },
          {
            name: 'Insertar Orden',
            to: paths.brandingOrderInsert,
            active: true
          },
          {
            name: 'Reportes',
            to: paths.brandingReports,
            active: true
          },
          {
            name: 'Analytics',
            to: paths.brandingAnalytics,
            active: true
          },
          {
            name: 'Objetivos',
            to: paths.brandingObjectives,
            active: true
          }
        ]
      },
      {
        name: 'Gestión de Cuentas',
        icon: 'credit-card',
        to: paths.accountsManagement,
        active: true
      },
      {
        name: 'Presupuestos',
        icon: 'dollar-sign',
        active: true,
        children: [
          {
            name: 'Ver Presupuestos',
            to: paths.budgetList,
            active: true
          },
          {
            name: 'Gestión de Cuentas',
            to: paths.budgetAccounts,
            active: true
          }
        ]
      },
      {
        name: 'Claro',
        icon: 'phone-alt',
        active: true,
        children: [
          {
            name: 'Reporte JIT SAC',
            to: paths.claroJit,
            active: true
          },
          {
            name: 'Reporte WhatsApp SAC',
            to: paths.claroWhatsapp,
            active: true
          }
        ]
      },
      {
        name: 'Dashboard',
        icon: 'chart-pie',
        active: true,
        children: [
          {
            name: 'Digital',
            to: paths.dashboardDigital,
            active: true
          },
          {
            name: 'ATL',
            to: paths.dashboardATL,
            active: true
          },
          {
            name: 'Mercado Publicitario',
            to: paths.mercadoPublicitario,
            active: true
          }
        ]
      },
      {
        name: 'Infomedia',
        icon: 'chart-mixed',
        active: true,
        children: [
          {
            name: 'Dashboard',
            to: paths.infomediaDashboard,
            active: true
          },
          {
            name: 'Mapeo de Datos',
            to: paths.infomediaMapping,
            active: true
          },
          {
            name: 'Competencia',
            to: paths.infomediaCompetencia,
            active: true
          },
          {
            name: 'Proyecciones ATL',
            to: paths.atlProyecciones,
            active: true
          }
        ]
      },
      {
        name: 'Ford Ventas',
        icon: 'car',
        to: paths.fordSales,
        active: true
      },
      {
        name: 'Ford Resultados',
        icon: 'chart-pie',
        to: paths.fordResultados,
        active: true
      },
      {
        name: 'Ford Reportes',
        icon: 'file-excel',
        to: paths.fordReportes,
        active: true
      },
      {
        name: 'Galaxy AI',
        icon: 'robot',
        to: paths.chat,
        active: true
      },
      {
        name: 'Data Factory',
        icon: 'database',
        to: paths.dataFactory,
        active: true
      },
      {
        name: 'Usuario',
        icon: 'user',
        active: true,
        children: [
          {
            name: 'Perfil',
            to: paths.userProfile,
            active: true
          }
        ]
      }
    ];
  }

  // Obtener breadcrumbs basado en la ruta actual
  generateBreadcrumbs(pathname, menus) {
    const breadcrumbs = [];
    
    // Función recursiva para encontrar el menú actual
    const findMenuItem = (items, path) => {
      for (const item of items) {
        if (item.path === path || item.to === path) {
          return item;
        }
        if (item.children) {
          const found = findMenuItem(item.children, path);
          if (found) {
            breadcrumbs.unshift(item);
            return found;
          }
        }
      }
      return null;
    };
    
    const currentItem = findMenuItem(menus, pathname);
    if (currentItem) {
      breadcrumbs.push(currentItem);
    }
    
    return breadcrumbs;
  }

  // Menús para desarrollo cuando no hay conexión al backend
  getDefaultMenus() {
    return [
      {
        name: 'Dashboard',
        icon: 'home',
        to: '/',
        active: true
      },
      {
        name: 'Usuario',
        icon: 'user',
        active: true,
        children: [
          {
            name: 'Perfil',
            to: '/user/profile',
            active: true
          }
        ]
      }
    ];
  }

  // Transformar menús de la base de datos al formato que espera Falcon
  transformMenusForFalcon(dbMenus) {
    if (!dbMenus || !Array.isArray(dbMenus)) {
      return [];
    }

    const transformed = dbMenus.map(menu => {
      const menuItem = {
        name: menu.name,
        icon: menu.icon || 'circle',
        to: menu.to || menu.path,
        active: menu.active !== false
      };
      
      // Solo agregar children si existe y tiene elementos
      if (menu.children && Array.isArray(menu.children) && menu.children.length > 0) {
        menuItem.children = this.transformMenusForFalcon(menu.children);
      }
      
      return menuItem;
    });
    
    return transformed;
  }
}

// Exportar instancia singleton
const menuService = new MenuService();
export default menuService;